import { useEffect, useState } from 'react';
import { navigate } from 'vike/client/router';

import { CoverageTrendTab } from './CoverageTrendChart';

import { request } from '@/renderer/utils/api';
import type { RequestSchema } from '@/types/request';

// Delivery-channel coverage (#1274) — the SMS→push transition scoreboard.
// Shared by the admin dashboard (summary panel) and the Users list (per-user
// channel icons + the active-service scope dropdown).

const COVERAGE_ENDPOINT = '/api/v1.1/admin/stats/coverage';

/** Fetch coverage metrics once. Read-only; fails quiet (returns null). */
export function useCoverage() {
  const [coverage, setCoverage] = useState<API.CoverageStats | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    request(COVERAGE_ENDPOINT as unknown as RequestSchema<API.CoverageStats>)
      .then((res) => setCoverage((res as any) ?? null))
      .catch(() => setCoverage(null))
      .finally(() => setLoading(false));
  }, []);
  return { coverage, loading };
}

// Tracked delivery channels (#1274): iOS, Android, Telegram — each a DISTINCT
// color, reused for the per-user icons and the per-channel coverage bars. Web/PWA
// is intentionally not a channel here. `active` is the lit icon color; `bar` is
// the matching bar fill; unlit icons render faint so a row reads at a glance.
const CHANNELS = [
  {
    key: 'ios',
    label: 'iOS',
    active: 'text-violet-600 dark:text-violet-400',
    bar: 'bg-violet-500',
    dot: 'bg-violet-500',
    viewBox: '0 0 384 512',
    path: 'M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z',
  },
  {
    key: 'android',
    label: 'Android',
    active: 'text-green-600 dark:text-green-400',
    bar: 'bg-green-500',
    dot: 'bg-green-500',
    viewBox: '0 0 576 512',
    path: 'M420.55 301.93a24 24 0 1 1 24-24 24 24 0 0 1-24 24m-265.1 0a24 24 0 1 1 24-24 24 24 0 0 1-24 24m273.7-144.48 47.94-83a10 10 0 1 0-17.27-10l-48.54 84.07a301.25 301.25 0 0 0-246.56 0L116.18 148.5a10 10 0 1 0-17.27 10l47.94 83C64.53 202.22 8.24 285.55 0 384h576c-8.24-98.45-64.54-181.78-146.85-226.55',
  },
  {
    key: 'telegram',
    label: 'Telegram',
    active: 'text-sky-500 dark:text-sky-400',
    bar: 'bg-sky-500',
    dot: 'bg-sky-500',
    viewBox: '0 0 448 512',
    path: 'M446.7 98.6l-67.6 318.8c-5 22.5-18.4 28.1-37.3 17.5l-103-75.9-49.7 47.8c-5.5 5.5-10.1 10.1-20.7 10.1l7.4-104.9 190.9-172.5c8.3-7.4-1.8-11.5-12.9-4.1L117.8 284 16.2 252.2c-22.1-6.9-22.5-22.1 4.6-32.7L418.2 66.4c18.4-6.9 34.5 4.1 28.5 32.2z',
  },
] as const;

export function ChannelIcons({ user, className = '' }: { user: API.User; className?: string }) {
  const platforms = new Set(user.pushPlatforms ?? []);
  const has = (key: string) =>
    key === 'telegram' ? !!user.hasTelegram : platforms.has(key as any);
  const anyReach = CHANNELS.some((c) => has(c.key));

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {CHANNELS.map((c) => {
        const on = has(c.key);
        return (
          <svg
            key={c.key}
            viewBox={c.viewBox}
            role="img"
            aria-label={`${c.label}: ${on ? 'yes' : 'no'}`}
            className={`size-4 ${on ? c.active : 'text-zinc-200 dark:text-zinc-700'}`}
            fill="currentColor"
          >
            <title>{`${c.label}: ${on ? 'yes' : 'no'}`}</title>
            <path d={c.path} />
          </svg>
        );
      })}
      {!anyReach && (
        <span className="ml-0.5 rounded bg-red-50 px-1 py-px text-[10px] font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">
          none
        </span>
      )}
    </div>
  );
}

const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

const fmtDate = (iso?: string | null) => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Human label for why a subscriber's email warning channel is dead. The reason
// is the SendGrid suppression event that killed it; absent reason + empty email
// means there is simply no email on file.
const suppressionLabel = (u: API.UnreachableSubscriber): string => {
  if (!u.email) {
    return 'no email on file';
  }
  const reasons: Record<string, string> = {
    unsubscribe: 'unsubscribed',
    group_unsubscribe: 'unsubscribed',
    spamreport: 'marked spam',
    bounce: 'bounced',
    dropped: 'dropped',
  };
  const base = u.suppressionReason
    ? reasons[u.suppressionReason] || u.suppressionReason
    : 'email suppressed';
  const when = fmtDate(u.suppressedAt);
  return when ? `${base} · ${when}` : base;
};

/**
 * Fully-unreachable action item (#1416). The subscribers no automated channel
 * can reach — no iOS/Android push, no Telegram, and no live email — so the
 * no-channel nudge (#1381) can't touch them and they need MANUAL outreach. Reads
 * as an action item (amber alert, not a stat), stays prominent while any exist,
 * and renders nothing when zero. Each row deep-links to the admin user page.
 */
export function UnreachableCallout({ coverage }: { coverage: API.CoverageStats | null }) {
  const [open, setOpen] = useState(false);
  const unreachable = coverage?.fullyUnreachable;
  if (!unreachable || unreachable.count <= 0) {
    return null;
  }
  const { count, users } = unreachable;

  return (
    <section className="rounded-xl border border-amber-500/40 bg-amber-50 p-4 shadow-xs dark:border-amber-400/30 dark:bg-amber-500/10">
      <div className="flex items-start gap-3">
        <svg
          viewBox="0 0 24 24"
          role="img"
          aria-label="Alert"
          className="mt-0.5 size-5 shrink-0 text-amber-600 dark:text-amber-400"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <title>Alert</title>
          <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
              {count.toLocaleString()} fully unreachable — reach manually
            </h2>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="shrink-0 text-xs font-medium text-amber-700 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-100"
            >
              {open ? 'Hide' : 'Show'}
            </button>
          </div>
          <p className="mt-0.5 text-xs text-amber-800/90 dark:text-amber-300/80">
            Active {count === 1 ? 'subscriber has' : 'subscribers have'} no push, no Telegram, and
            no live email, so the automated no-channel nudge can't reach them. Contact them
            directly.
          </p>

          {open && (
            <ul className="mt-3 divide-y divide-amber-500/20 overflow-hidden rounded-lg border border-amber-500/20 bg-white/60 dark:divide-amber-400/10 dark:border-amber-400/10 dark:bg-zinc-900/40">
              {users.map((u) => {
                const href = `/admin/users/${u.id}`;
                return (
                  <li key={u.id}>
                    <a
                      href={href}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(href);
                      }}
                      className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 px-3 py-2 transition hover:bg-amber-100/60 dark:hover:bg-amber-500/10"
                    >
                      <span className="text-xs font-medium text-zinc-900 dark:text-zinc-100">
                        {u.name}
                      </span>
                      <span className="text-xs text-zinc-500 dark:text-zinc-400">
                        {u.email || '—'}
                      </span>
                      <span className="rounded bg-amber-500/15 px-1.5 py-px text-[10px] font-medium text-amber-700 dark:text-amber-300">
                        {suppressionLabel(u)}
                      </span>
                      {fmtDate(u.lastOrderAt) && (
                        <span className="text-[11px] text-zinc-400 dark:text-zinc-500">
                          last order {fmtDate(u.lastOrderAt)}
                        </span>
                      )}
                      <span className="ml-auto text-xs text-amber-700 dark:text-amber-300">→</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// A coverage bar toward the goal, composed of channel-colored segments (#1320):
// each reachable subscriber is attributed to exactly ONE channel by priority
// (iOS > Android > Telegram), so the segments sum to the any-push total with no
// double-counting and read against the same iOS-violet / Android-green /
// Telegram-sky palette used everywhere else. The empty tail is the at-risk gap.
// A tick marks the goal line — emerald once met, zinc while below.
function CoverageBar({ counts, goal }: { counts: API.CoverageCounts; goal: number }) {
  const { total } = counts;
  const met = pct(counts.anyChannel, total) >= goal;
  return (
    <div className="relative flex h-2 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
      {CHANNELS.map((c) => {
        const n = (counts as any)[`${c.key}Attr`] as number;
        const w = total > 0 ? (n / total) * 100 : 0;
        if (w <= 0) {
          return null;
        }
        return (
          <div
            key={c.key}
            className={`h-full ${c.bar}`}
            style={{ width: `${w}%` }}
            title={`${c.label}: ${n.toLocaleString()}`}
          />
        );
      })}
      <div
        className={`absolute top-0 h-full w-px ${
          met ? 'bg-emerald-500' : 'bg-zinc-400 dark:bg-zinc-500'
        }`}
        style={{ left: `${Math.min(100, goal)}%` }}
        title={`Goal ${goal}%`}
      />
    </div>
  );
}

// Legend for the stacked coverage bar (#1320): each channel's attributed share,
// in its own distinct color. Uses the priority-attributed counts (iOS > Android >
// Telegram), so a colored dot + count + share line up exactly with the segments
// of the bar and the parts sum to the any-push total (no double-counting).
function ChannelBreakdown({ counts }: { counts: API.CoverageCounts }) {
  const attr = { ios: counts.iosAttr, android: counts.androidAttr, telegram: counts.telegramAttr };
  return (
    <div className="flex flex-col gap-1.5">
      {CHANNELS.map((c) => {
        const n = attr[c.key];
        const p = pct(n, counts.total);
        return (
          <div key={c.key} className="flex items-center gap-2">
            <span className={`size-1.5 shrink-0 rounded-full ${c.dot}`} aria-hidden />
            <span className="w-14 shrink-0 text-xs text-zinc-500 dark:text-zinc-400">
              {c.label}
            </span>
            <span className="w-12 shrink-0 text-right text-xs text-zinc-600 tabular-nums dark:text-zinc-300">
              {n.toLocaleString()}
            </span>
            <span className="w-9 shrink-0 text-right text-xs font-medium text-zinc-800 tabular-nums dark:text-zinc-200">
              {p}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Coverage summary — overall any-push % vs goal plus a per-active-service grid.
 * `onPick(serviceId, channel)` fires when a service name or an "at risk" count is
 * clicked; the caller decides what to do (filter in place, or navigate to the
 * Users list). `activeService` highlights the matching service card, if any.
 */
export function CoveragePanel({
  coverage,
  activeService = '',
  onPick,
}: {
  coverage: API.CoverageStats | null;
  activeService?: string;
  onPick: (serviceId: string, channel: string) => void;
}) {
  const [open, setOpen] = useState(true);
  // Two views of the same box: the point-in-time snapshot ('coverage') and the
  // trend over time ('trend', #1473). The trend lives here as a tab rather than a
  // separate card so the two stay together.
  const [tab, setTab] = useState<'coverage' | 'trend'>('coverage');
  if (!coverage?.overall) {
    return null;
  }

  const { overall, services, goal } = coverage;
  const goalPct = Math.round((goal ?? 0.9) * 100);
  const overallPct = pct(overall.anyChannel, overall.total);
  const overallMet = overallPct >= goalPct;

  return (
    <section className="rounded-xl border border-zinc-950/10 bg-white p-4 shadow-xs dark:border-white/10 dark:bg-zinc-900">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-zinc-950 dark:text-white">Delivery coverage</h2>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-zinc-400">
            {tab === 'coverage'
              ? `Active subscribers reachable by push · goal ${goalPct}%`
              : 'How coverage is trending over time'}
          </p>
          {/* Tabs: point-in-time snapshot vs the trend over time (#1473). */}
          <div className="mt-2 flex gap-1">
            {(
              [
                ['coverage', 'Current'],
                ['trend', 'Trend'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`rounded-md px-2.5 py-1 text-xs font-medium transition ${
                  tab === key
                    ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
                    : 'text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="shrink-0 text-xs font-medium text-zinc-500 hover:text-zinc-800 dark:text-zinc-400 dark:hover:text-zinc-200"
        >
          {open ? 'Hide' : 'Show'}
        </button>
      </div>

      {open && tab === 'trend' && (
        <div className="mt-3">
          <CoverageTrendTab />
        </div>
      )}

      {open && tab === 'coverage' && (
        <>
          {/* Overall roll-up */}
          <div className="mt-3 flex flex-wrap items-end gap-x-6 gap-y-2">
            <div>
              <div className="flex items-baseline gap-2">
                <span
                  className={`text-3xl font-semibold ${
                    overallMet
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-amber-600 dark:text-amber-400'
                  }`}
                >
                  {overallPct}%
                </span>
                <span className="text-xs text-zinc-500 dark:text-zinc-400">any push channel</span>
              </div>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                {overall.anyChannel.toLocaleString()} of {overall.total.toLocaleString()} active
                subscribers
              </p>
            </div>

            <ChannelBreakdown counts={overall} />

            {overall.none > 0 && (
              <button
                type="button"
                onClick={() => onPick('any', 'none')}
                className="ml-auto rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-600 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                title="Show active subscribers with no push channel"
              >
                {overall.none.toLocaleString()} at risk →
              </button>
            )}
          </div>
          <div className="mt-2">
            <CoverageBar counts={overall} goal={goalPct} />
          </div>

          {/*
            Web Push (browser/PWA) adoption (#1669) — informational only. Kept
            OUT of the any-push bar/breakdown above: it isn't a live delivery
            channel yet, so folding it into "reachable" would overstate coverage.
            Shown only once there are subscribers so the panel stays clean
            pre-launch.
          */}
          {overall.webpush > 0 && (
            <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
              <span className="font-medium text-zinc-700 dark:text-zinc-300">Web push</span>{' '}
              {overall.webpush.toLocaleString()} subscribed ({pct(overall.webpush, overall.total)}%
              of active) · not counted toward reachable yet
            </p>
          )}

          {/* Per active service */}
          {services.length > 0 && (
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {services.map((svc) => {
                const p = pct(svc.anyChannel, svc.total);
                const met = p >= goalPct;
                const selected = String(svc.serviceId) === activeService;
                return (
                  <div
                    key={svc.serviceId}
                    className={`rounded-lg border p-2.5 ${
                      selected
                        ? 'border-emerald-500/40 bg-emerald-50/40 dark:border-emerald-400/30 dark:bg-emerald-500/10'
                        : 'border-zinc-950/5 dark:border-white/5'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => onPick(String(svc.serviceId), '')}
                        className="truncate text-xs font-medium text-zinc-800 hover:underline dark:text-zinc-200"
                        title={`Filter to ${svc.name} subscribers`}
                      >
                        {svc.name}
                      </button>
                      <span
                        className={`shrink-0 text-xs font-semibold ${
                          met
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-amber-600 dark:text-amber-400'
                        }`}
                      >
                        {p}%
                      </span>
                    </div>
                    <div className="mt-1.5">
                      <CoverageBar counts={svc} goal={goalPct} />
                    </div>
                    <div className="mt-1.5 flex items-center justify-between text-[11px] text-zinc-500 dark:text-zinc-400">
                      <span>
                        {svc.anyChannel.toLocaleString()}/{svc.total.toLocaleString()} reachable
                      </span>
                      {svc.none > 0 && (
                        <button
                          type="button"
                          onClick={() => onPick(String(svc.serviceId), 'none')}
                          className="rounded bg-red-50 px-1 font-medium text-red-600 transition hover:bg-red-100 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20"
                          title={`Show ${svc.name} subscribers with no push channel`}
                        >
                          {svc.none} at risk →
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}
